<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <div class="toolbar">
            <div class="toolbar__label">
                Processors available to be assigned to blocks
            </div>
            <div class="actions">
                <i class="actions__item zmdi zmdi-search" @click="enableSearch"></i>
            </div>

            <div class="toolbar__search" :class="searchEnabled ? 'd-block' : 'd-none'">
                <input type="text" placeholder="Search..." ref="searchInput" v-model="searchTerm">
                <i class="toolbar__search__close zmdi zmdi-long-arrow-left" @click="disableSearch"></i>
            </div>
        </div>

        <div class="card">
            <div class="card-header bg-primary text-white">
                Processor
            </div>

            <div class="form-control form-control-lg bg-light form-filter d-flex">
                <VueSelect
                        v-model="region"
                        :options="allRegions"
                        placeholder="Filter by Region"
                        label="name"
                        track-by="id"
                ></VueSelect>
                <VueSelect
                        v-model="cropType"
                        :options="allCropTypes"
                        placeholder="Filter by CropType"
                        label="name"
                        track-by="id"
                ></VueSelect>
            </div>

            <b-table small hover stacked="md" :items="filteredData" :fields="table.fields" sort-by="name" :show-empty="true">

                <template slot="name" slot-scope="row">
                    <div class="custom-control custom-checkbox"
                         :v-b-tooltip="{
                            placement: 'left',
                            trigger: 'hover',
                            title: row.item.linkedBlocks.length > 0 ? 'Processor linked directly to blocks' : ''
                         }">
                        <input class="custom-control-input"
                               :id="`processor${row.item.id}`" type="checkbox"
                               :checked="row.item.isGlobalLink || row.item.linkedBlocks.length > 0"
                               :disabled="row.item.linkedBlocks.length > 0"
                               @click.prevent="toggleRow(row.item, row.index)"
                        >
                        <label class="custom-control-label" :for="`processor${row.item.id}`">{{ row.item.name }}</label>
                    </div>
                </template>

                <template slot="cropTypes" slot-scope="row">
                     <span class="badge badge-purple text-white mr-1" v-for="cropType in row.item.cropTypes">
                        {{ cropType.name }}
                    </span>
                </template>

                <template slot="number" slot-scope="row">
                    <input type="text" :value="row.item.number" @change="updateProcessorNumber(row.item.id, $event)" class="form-control" placeholder="Enter processor number..."
                           v-if="row.item.isGlobalLink || row.item.linkedBlocks.length > 0">
                </template>

                <template slot="regions" slot-scope="row">
                    <span class="badge badge-info mr-1" v-for="region in row.item.regions">
                        {{ region.name }}
                    </span>
                </template>

                <template slot="blocks" slot-scope="row">
                    <span class="badge badge-success" v-if="row.item.isGlobalLink">All Blocks</span>
                    <template v-for="blockData in row.item.linkedBlocks" v-if="row.item.linkedBlocks.length > 0">
                        <span class="badge mr-1" :class="{
                        'badge-danger': blockData.isExclusion,
                        'badge-light': !blockData.isExclusion,
                        }"
                              v-b-tooltip="blockData.isExclusion ? 'Excluded' : ''"
                        >
                        {{ blockData.block.name }}
                    </span>
                    </template>
                </template>
            </b-table>

        </div>
    </div>
</template>
<script>
import createLinkMutation from '@views/Setup/ProcessorManager/mutationCreateGrowerProcessorLink.graphql';
import deleteLinkMutation from '@views/Setup/ProcessorManager/mutationDeleteGrowerProcessorLink.graphql';
import updateNumberMutation from '@views/Setup/ProcessorManager/mutationUpdateGrowerProcessorNumber.graphql';
import listCropTypesQuery from '@views/Setup/ProcessorManager/queryListCropTypes.graphql';
import listLinkedProcessorsQuery from '@views/Setup/ProcessorManager/queryListGrowerLinkedProcessors.graphql';
import listProcessorNumbersQuery from '@views/Setup/ProcessorManager/queryListGrowerProcessorNumbers.graphql';
import listProcessorsQuery from '@views/Setup/ProcessorManager/queryListProcessors.graphql';
import listRegionsQuery from '@views/Setup/ProcessorManager/queryListRegions.graphql';

/**
 * @property {String} searchTerm
 */
export default {
  name: 'ProcessorManager',
  props: ['currentUser'],
  apollo: {
    allProcessors: {
      query: listProcessorsQuery,
      update: data => {
        return data.filterProcessors.data;
      },
      variables() {
        const variables = {
          input: {},
        };
        if (this.region) {
          variables.input.region_id = this.region.id;
        }
        if (this.cropType) {
          variables.input.crop_type_id = this.cropType.id;
        }

        return variables;
      },
    },
    linkedProcessors: {
      query: listLinkedProcessorsQuery,
      update: data => data.filterGrowerProcessorLinks,
    },
    processorNumbers: {
      query: listProcessorNumbersQuery,
      update: data => data.filterGrowerProcessorNumbers,
    },
    allRegions: {
      query: listRegionsQuery,
      update: data => data.filterRegions.data,
    },
    allCropTypes: {
      query: listCropTypesQuery,
      update: data => data.filterCropTypes.data,
    },
  },
  data() {
    return {
      allProcessors: [],
      linkedProcessors: [],
      processorNumbers: [],
      searchEnabled: false,
      searchTerm: '',
      loading: 0,
      allCropTypes: [],
      allRegions: [],
      region: this.currentUser.region,
      cropType: null,
    };
  },
  computed: {
    table() {
      return {
        fields: [
          { key: 'name', label: 'Processor', sortable: true },
          { key: 'number', label: 'Processor Number' },
          { key: 'cropTypes', label: 'CropTypes', sortable: true },
          { key: 'regions', label: 'Regions', sortable: true },
          { key: 'blocks', label: 'Blocks' },
        ],
      };
    },
    filteredData() {
      const subjectMatchesSearch = subject => {
        return (
          this.searchTerm.length === 0 ||
          subject.name
            .toLowerCase()
            .includes(this.searchTerm.toLowerCase())
        );
      };

      return this.allProcessors
        .filter(subjectMatchesSearch)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(processor => ({
          ...processor,
          number: this.processorNumber(processor.id),
          isGlobalLink: this.linkedGlobally(processor.id),
          linkedBlocks: this.linkedBlocks(processor.id) || [],
        }));
    },
  },
  mounted() {
  },
  methods: {
    enableSearch() {
      this.searchEnabled = true;
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    disableSearch() {
      this.searchTerm = '';
      this.searchEnabled = false;
    },
    updateProcessorNumber(processorId, event) {
      const newNumber = event.target.value;
      event.preventDefault();
      const existingIndex = this.processorNumbers.findIndex(item => item.processor.id === processorId);

      this.$apollo.mutate({
        mutation: updateNumberMutation,
        variables: {
          input: {
            processor_id: processorId,
            number: newNumber,
          },
        },
        update: (store, { data: { updateGrowerProcessorNumber: link } }) => {
          const data = store.readQuery({ query: listProcessorNumbersQuery });
          if (existingIndex !== -1) {
            data.filterGrowerProcessorNumbers[existingIndex] = link;
          } else {
            data.filterGrowerProcessorNumbers.push(link);
          }
          store.writeQuery({ query: listProcessorNumbersQuery, data });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateGrowerProcessorNumber: {
            __typename: 'GrowerProcessorNumber',
            id: existingIndex === -1 ? -1 : this.processorNumbers[existingIndex].id,
            processor: {
              __typename: 'Processor',
              id: processorId,
            },
            number: newNumber,
          },
        },
      });
    },
    processorNumber(processorId) {
      const link = this.processorNumbers.find(item => item.processor.id === processorId);
      return link ? link.number : '';
    },
    linkedToSpecificBlocks(processorId) {
      return this.linkedProcessors.some(
        link => link.processor.id === processorId && link.block !== null,
      );
    },
    linkedGlobally(processorId) {
      return this.linkedProcessors.some(
        link => link.processor.id === processorId && link.block === null,
      );
    },
    linkedBlocks(processorId) {
      return this.linkedProcessors
        .filter(link => link.processor.id === processorId && link.block !== null)
        .reduce((list, link) => [...list, { block: link.block, isExclusion: link.is_exclusion }], []);
    },
    toggleRow(item, index) {
      if (item.linkedBlocks.length === 0) {
        this.handleProcessorToggle(item);
      }
    },
    handleProcessorToggle(processor) {
      if (this.linkedGlobally(processor.id)) {
        this.unlinkProcessor(processor);
      } else {
        this.linkProcessor(processor);
      }
    },
    unlinkProcessor(processor) {
      const linkToRemove = this.linkedProcessors.find(
        link =>
          link.processor.id === processor.id && link.block === null,
      );

      this.$apollo.mutate({
        mutation: deleteLinkMutation,
        variables: {
          input: {
            processor_id: processor.id,
          },
        },
        update: (store, { data: { deleteGrowerProcessorLink: removedLink } }) => {
          const data = store.readQuery({ query: listLinkedProcessorsQuery });
          const indexToRemove = data.filterGrowerProcessorLinks.findIndex(item => item.id === removedLink.id);
          if (indexToRemove !== -1) {
            data.filterGrowerProcessorLinks.splice(indexToRemove, 1);
            store.writeQuery({ query: listLinkedProcessorsQuery, data });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteGrowerProcessorLink: {
            ...linkToRemove,
          },
        },
      });
    },
    linkProcessor(processor) {
      this.$apollo.mutate({
        mutation: createLinkMutation,
        variables: {
          input: {
            processor_id: processor.id,
          },
        },
        update: (store, { data: { createGrowerProcessorLink: link } }) => {
          const data = store.readQuery({
            query: listLinkedProcessorsQuery,
          });
          data.filterGrowerProcessorLinks.push(link);
          store.writeQuery({
            query: listLinkedProcessorsQuery,
            data,
          });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          createGrowerProcessorLink: {
            __typename: 'GrowerProcessorLink',
            id: -1,
            processor: processor,
            block: null,
            is_exclusion: false,
          },
        },
      });
    },
  },
};
</script>
